import { getDeepError, UserRejectedRequestError } from '@polaris/core/utils'
import { atom } from 'jotai'
import {
  errorAtom,
  isAbortedAtom,
  isGasStepActiveAtom,
  isSuccessAtom,
  isTradeStartedAtom,
  isTransferringFromAutopilotToDestinationAtom,
  namadaUnshieldStepAtom,
  routeMachineSnapshotAtom,
  shouldExecuteNamadaUnshieldAtom,
  shouldTransferFundsToAutopilotWalletAtom,
  shouldUseGasStationAtom,
  tradeStepAtom as tradeStepRouteMachineAtom,
  transferFundsBackStepAtom,
  transferFundsToHotkeyStepAtom,
} from '@/src/stores/route-machine/atoms'

export enum TradeStep {
  GasStation = 'GasStation',
  StartTrade = 'StartTrade',
  Trade = 'Trade',
  Summary = 'Summary',
  Success = 'Success',
  Recovery = 'Recovery',
  RecoverySuccess = 'RecoverySuccess',
}

export const stepsNeededAtom = atom((get) => {
  const shouldUseGasStation = get(shouldUseGasStationAtom)
  const isSuccess = get(isSuccessAtom)
  const isAborted = get(isAbortedAtom)
  const snapshot = get(routeMachineSnapshotAtom)

  const steps = []
  if (shouldUseGasStation) steps.push(TradeStep.GasStation)
  steps.push(TradeStep.StartTrade)
  steps.push(TradeStep.Trade)
  steps.push(TradeStep.Summary)

  if (snapshot.hasTag('aborting')) {
    steps.push(TradeStep.Recovery)
    steps.push(TradeStep.RecoverySuccess)
  }

  if (isSuccess || isAborted) steps.push(TradeStep.Success)

  return steps
})

export const activeStepsAtom = atom((get) => {
  const isGasStepActive = get(isGasStepActiveAtom)
  const isTradeStarted = get(isTradeStartedAtom)
  const isSuccess = get(isSuccessAtom)
  const error = get(errorAtom)
  const snapshot = get(routeMachineSnapshotAtom)

  const steps = new Map<TradeStep, boolean>()

  if (snapshot.hasTag('aborting')) {
    const isRecoverySuccess = snapshot.value === 'FundsRecoverySuccess'

    steps.set(TradeStep.Recovery, !isRecoverySuccess)
    steps.set(TradeStep.RecoverySuccess, isRecoverySuccess)
  }

  steps.set(TradeStep.Success, isSuccess)
  steps.set(
    TradeStep.Summary,
    !!error && !(getDeepError(error) instanceof UserRejectedRequestError),
  )
  steps.set(TradeStep.GasStation, isGasStepActive)
  steps.set(TradeStep.StartTrade, !isTradeStarted)
  steps.set(TradeStep.Trade, isTradeStarted)

  return steps
})

export const startTradeStepAtom = atom((get) => {
  // Priority 1: Namada unshield (Step 1)
  if (get(shouldExecuteNamadaUnshieldAtom)) return get(namadaUnshieldStepAtom)

  // Priority 2: Hotkey transfer
  if (get(shouldTransferFundsToAutopilotWalletAtom))
    return get(transferFundsToHotkeyStepAtom)

  // Default: Trade step
  const tradeStep = get(tradeStepRouteMachineAtom)
  if (!get(isTradeStartedAtom)) return tradeStep

  return {
    isActive: false,
    isSigning: false,
    isLoading: false,
    isSuccess: true,
    isError: false,
    error: undefined,
    transferDetails: undefined,
    isUserSigning: false,
  }
})

export const tradeStepAtom = atom((get) => {
  const isTransferringFromHotkeyToDestination = get(
    isTransferringFromAutopilotToDestinationAtom,
  )

  if (isTransferringFromHotkeyToDestination)
    return {
      ...get(transferFundsBackStepAtom),
      isUserSigning: false,
      currentTradeStepDetails: undefined,
      transferDetails: undefined,
    }

  if (get(isTradeStartedAtom)) return get(tradeStepRouteMachineAtom)

  return {
    isActive: false,
    isSigning: false,
    isLoading: false,
    isSuccess: false,
    isError: false,
    error: undefined,
    currentTradeStepDetails: undefined,
    transferDetails: undefined,
    isUserSigning: false,
  }
})

type TradeStepsViewMode = 'overview' | 'current-step'

export const viewModeAtom = atom<TradeStepsViewMode>('current-step')

export const isCurrentStepModeAtom = atom((get) => {
  const viewMode = get(viewModeAtom)

  return viewMode === 'current-step'
})
